/**
 * Optimizer model whitelist — mirrors BE ALLOWED_OPTIMIZATION_MODELS verbatim.
 * Keep in sync with mutagent/src/modules/optimizations/service.ts.
 * The parity test (src/__tests__/optimizer-whitelist-parity.test.ts) catches drift.
 */
export const ALLOWED_OPTIMIZATION_MODELS = [
  // OpenAI - GPT 5.2 to 5.4 (strong models only)
  'gpt-5.2',
  'gpt-5.4',
  // Anthropic - Sonnet 4.6, Opus 4.6 (including 1M context variants)
  'claude-sonnet-4-6',
  'claude-sonnet-4-6[1m]',
  'claude-opus-4-6',
  'claude-opus-4-6[1m]',
  // Bedrock variants (anthropic. prefix required by AWS model ID format)
  'anthropic.claude-sonnet-4-6',
  'anthropic.claude-opus-4-6',
  'anthropic.claude-opus-4-6-v1',
  'anthropic.claude-sonnet-4-5-20250929-v1:0',
  'claude-opus-4-5',
  'anthropic.claude-opus-4-5-20251101-v1:0',
  // Google
  'gemini-3.1-pro-preview',
  'gemini-3-flash-preview',
] as const;

const WHITELIST_BY_PROVIDER: Record<string, string[]> = {
  openai: ALLOWED_OPTIMIZATION_MODELS.filter(m => m.startsWith('gpt-')),
  anthropic: ALLOWED_OPTIMIZATION_MODELS.filter(m => m.startsWith('claude-')),
  bedrock: ALLOWED_OPTIMIZATION_MODELS.filter(m => m.startsWith('anthropic.')),
  google: ALLOWED_OPTIMIZATION_MODELS.filter(m => m.startsWith('gemini-')),
};

/**
 * Validate that a model is allowed for the optimization slot.
 * Throws with a helpful error listing allowed models grouped by provider.
 */
export function validateOptimizerModel(model: string): void {
  if ((ALLOWED_OPTIMIZATION_MODELS as readonly string[]).includes(model)) return;

  const grouped = Object.entries(WHITELIST_BY_PROVIDER)
    .filter(([, models]) => models.length > 0)
    .map(([provider, models]) => `  ${provider}: ${models.join(', ')}`)
    .join('\n');

  throw new Error(
    `Model "${model}" is not allowed for the optimization slot.\n` +
    `Allowed optimization models by provider:\n${grouped}\n` +
    `Note: --exec-model and --eval-model are unrestricted.`
  );
}
